import useSWR from 'swr';
import Card from 'components/card';
import { ReadPermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { solvencyService } from 'views/services/solvencyService';
import { ICoinSolvency } from 'views/types/solvency';

const formatAmount = (raw: string, decimals: number): string => {
  if (!raw) return '0';
  const negative = raw.startsWith('-');
  let digits = (negative ? raw.slice(1) : raw).replace(/[^0-9]/g, '');
  if (!digits) return '0';
  if (decimals <= 0) return `${negative ? '-' : ''}${digits}`;
  digits = digits.padStart(decimals + 1, '0');
  const whole = digits.slice(0, digits.length - decimals);
  const frac = digits
    .slice(digits.length - decimals)
    .slice(0, 6)
    .replace(/0+$/, '');
  return `${negative ? '-' : ''}${whole}${frac ? '.' + frac : ''}`;
};

const headerClass =
  'border-b border-gray-200 pb-2 pr-4 text-start text-sm font-bold text-gray-600 dark:border-white/20 dark:text-white';
const cellClass = 'py-3 pr-4 text-sm text-navy-700 dark:text-white';

const stateClass = (state: string): string => {
  switch (state) {
    case 'GREEN':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30';
    case 'YELLOW':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30';
    case 'RED':
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30';
    default:
      return 'bg-red-100 text-red-700 dark:bg-red-900/30';
  }
};

export const Solvency = () => {
  const { data, isLoading } = useSWR(
    '/api/admin/solvency',
    () => solvencyService.getSolvency(),
    { refreshInterval: 30000 }
  );

  const coins: ICoinSolvency[] = data?.coins ?? [];
  const shortCount = coins.filter(
    c => !c.partial && c.ratio !== null && c.ratio < 1
  ).length;
  const ops = data?.operations;

  return (
    <ReadPermissionGate
      resource={PERMISSION_RESOURCES.SOLVENCY}
      fallback={
        <div className="mt-5 text-center text-gray-600 dark:text-white">
          You don't have access to the Solvency page.
        </div>
      }
    >
      <div className="mt-3">
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card extra="p-4">
            <p className="text-sm font-medium text-gray-600">Coins tracked</p>
            <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
              {coins.length}
            </h4>
          </Card>
          <Card extra="p-4">
            <p className="text-sm font-medium text-gray-600">
              Coins short (ratio &lt; 1)
            </p>
            <h4
              className={`text-2xl font-bold ${
                shortCount ? 'text-red-500' : 'text-green-500'
              }`}
            >
              {shortCount}
            </h4>
          </Card>
          <Card extra="p-4">
            <p className="text-sm font-medium text-gray-600">Last computed</p>
            <h4 className="text-lg font-bold text-navy-700 dark:text-white">
              {data?.computedAt
                ? new Date(data.computedAt).toLocaleString()
                : '—'}
            </h4>
          </Card>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-4 md:grid-cols-4">
          <Card extra="p-4">
            <p className="text-sm font-medium text-gray-600">
              Pending withdrawals
            </p>
            <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
              {ops?.pendingWithdrawals ?? '—'}
            </h4>
          </Card>
          <Card extra="p-4">
            <p className="text-sm font-medium text-gray-600">
              Waiting for liquidity
            </p>
            <h4
              className={`text-2xl font-bold ${
                ops?.waitingForLiquidity
                  ? 'text-amber-500'
                  : 'text-navy-700 dark:text-white'
              }`}
            >
              {ops?.waitingForLiquidity ?? '—'}
            </h4>
          </Card>
          <Card extra="p-4">
            <p className="text-sm font-medium text-gray-600">Pending swaps</p>
            <h4 className="text-2xl font-bold text-navy-700 dark:text-white">
              {ops?.pendingSwaps ?? '—'}
            </h4>
          </Card>
          <Card extra="p-4">
            <p className="text-sm font-medium text-gray-600">
              Blocked deposits
            </p>
            <h4
              className={`text-2xl font-bold ${
                ops?.blockedDeposits
                  ? 'text-red-500'
                  : 'text-navy-700 dark:text-white'
              }`}
            >
              {ops?.blockedDeposits ?? '—'}
            </h4>
          </Card>
        </div>

        <Card extra="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-lg font-bold text-navy-700 dark:text-white">
              Held vs. owed per coin
            </h4>
            {isLoading && (
              <span className="text-sm text-gray-500">Loading…</span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={headerClass}>Coin</th>
                  <th className={headerClass}>Treasury</th>
                  <th className={headerClass}>MEXC (LP)</th>
                  <th className={headerClass}>Held</th>
                  <th className={headerClass}>Owed</th>
                  <th className={headerClass}>Surplus / Shortfall</th>
                  <th className={headerClass}>Reserve ratio</th>
                  <th className={headerClass}>State</th>
                </tr>
              </thead>
              <tbody>
                {coins.map(coin => {
                  const partial = !!coin.partial;
                  const unavail = (src: string) =>
                    partial && (coin.unavailable ?? []).includes(src);
                  const short =
                    !partial && coin.ratio !== null && coin.ratio < 1;
                  const surplusNegative = coin.surplus.startsWith('-');
                  const na = <span className="text-gray-400">n/a</span>;
                  return (
                    <tr
                      key={coin.symbol}
                      className={short ? 'bg-red-50 dark:bg-red-900/20' : ''}
                    >
                      <td className={`${cellClass} font-bold`}>
                        {coin.symbol}
                        {partial && (
                          <span
                            className="ml-1 cursor-help text-amber-500"
                            title={`Partial data — could not read: ${(
                              coin.unavailable ?? []
                            ).join(', ')}`}
                          >
                            ⚠
                          </span>
                        )}
                      </td>
                      <td className={cellClass}>
                        {unavail('treasury')
                          ? na
                          : formatAmount(coin.treasury, coin.decimals)}
                      </td>
                      <td className={cellClass}>
                        {unavail('mexc')
                          ? na
                          : formatAmount(coin.mexc, coin.decimals)}
                      </td>
                      <td className={cellClass}>
                        {partial ? na : formatAmount(coin.held, coin.decimals)}
                      </td>
                      <td className={cellClass}>
                        {unavail('userBalances') || unavail('blockedFunds')
                          ? na
                          : formatAmount(coin.owed, coin.decimals)}
                      </td>
                      <td
                        className={`${cellClass} ${
                          !partial && surplusNegative
                            ? 'text-red-500'
                            : 'text-green-600'
                        }`}
                      >
                        {partial
                          ? na
                          : formatAmount(coin.surplus, coin.decimals)}
                      </td>
                      <td
                        className={`${cellClass} font-bold ${
                          short ? 'text-red-500' : ''
                        }`}
                      >
                        {coin.ratio === null ? '—' : coin.ratio.toFixed(4)}
                      </td>
                      <td className={cellClass}>
                        <span
                          className={`rounded px-2 py-1 text-xs font-bold ${
                            partial
                              ? 'bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300'
                              : stateClass(coin.state)
                          }`}
                        >
                          {partial ? 'PARTIAL' : coin.state}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {!isLoading && coins.length === 0 && (
                  <tr>
                    <td
                      className="py-4 text-center text-sm text-gray-500"
                      colSpan={8}
                    >
                      No data.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card extra="mt-4 p-4">
          <h4 className="mb-2 text-lg font-bold text-navy-700 dark:text-white">
            Operational infrastructure
          </h4>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <div className="text-sm text-navy-700 dark:text-white">
              <span className="font-semibold">AMLBot credit:</span>{' '}
              {data?.infra?.amlBotCredit ?? '—'}
            </div>
            {(data?.infra?.gasStations ?? []).map(g => (
              <div
                key={`${g.network}-${g.symbol}`}
                className="text-sm text-navy-700 dark:text-white"
              >
                <span className="font-semibold">{g.network} gas:</span>{' '}
                {formatAmount(g.balance, g.decimals)} {g.symbol}
              </div>
            ))}
          </div>
        </Card>
      </div>
    </ReadPermissionGate>
  );
};

export default Solvency;
